import { TpETLComponentTypeTemplate } from '../../../api/models/etl';
import { getUserError } from '../../../components/miscUtils';
import { manifestETLAppendTables } from './append-tables/etlAppendTables.Manifest';
import { manifestETLApplyExpressionTable } from './apply-expression-table/etlApplyExpressionTable.Manifest';
import { manifestETLApplyLLMTable } from './apply-llm-table/etlApplyLLMTable.Manifest';
import { manifestETLApplyLLMText } from './apply-llm-text/etlApplyLLMText.Manifest';
import { manifestETLDefineText } from './define-text/etlDefineText.Manifest';
import { manifestETLFetchPubMedArticle } from './fetch-full-pubmed-article/etlFetchPubMedArticle.Manifest';
import { manifestETLFilterTable } from './filter-table/etlFilterTable.Manifest';
import { manifestETLImportDataContext } from './import-datacontext/etlImportDataContext';
import { TpETLComponentTypeManifest } from './interfaces';
import { manifestETLJsonToTable } from './json-to-table/etlJsonToTable.Manifest';
import { manifestETLJsonNestedExplode } from './nested-json-to-table-explode/etlNestedJsonToTableExplode.Manifest';
import { manifestNormalizeGeneNames } from './normalize-gene-names/etlNormalizeGeneNames.Manifest';
import { manifestETLPublicationCredibility } from './publication-credibility/etlPublicationCredibility.Manifest';
import { manifestETLQueryPubMed } from './query-pubmed/etlQueryPubMed.Manifest';
import { manifestETLQueryPubTator } from './query-pubtator/etlQueryPubTator.Manifest';
import { manifestETLTableAggregate } from './table-aggregate/etlTableAggregate.Manifest';
import { manifestETLTableJoin } from './table-join/etlTableJoin.Manifest';
import { manifestETLTablePivot } from './table-pivot/etlTablePivot.Manifest';
import { manifestETLTopicClustering } from './topic-clustering/etlTopicClustering.Manifest';
import { manifestETLSelectDataTable } from './select-data-table/etlSelectDataTable';
import { manifestETLTablesToMatrix } from './tables-to-matrix/etlTablesToMatrix.Manifest';
import { manifestETLMatrixToTables } from './matrix-to-tables/etlMatrixToTables.Manifest';
import { manifestETLMatrixExtractFeatures } from './matrix-extract-features/etlMatrixExtractFeatures.Manifest';
import { manifestETLApplyProjectionUmap } from './apply-projection-umap/etlApplyProjectionUmap.Manifest';
import { manifestETLApplyPcaMatrix } from './apply-pca-matrix/etlApplyPcaMatrix.Manifest';
import { manifestETLDgeAnalysis } from './dge-analysis/etlDgeAnalysis.Manifest';
import { manifestETLApplySummaryStatMatrix } from './apply-summarystat-matrix/etlApplySummaryStatMatrix.Manifest';
import { manifestETLFilterMatrix } from './filter-matrix/etlFilterMatrix.Manifest';
import { manifestETLTableDropColumns } from './table-drop-columns/etlTableDropColumns.Manifest';
import { manifestQueryTaxonInfo } from './query-taxon-info/etlQueryTaxonInfo.Manifest';
import { getManifestFromTemplate } from './execute-external-tool/etlExecuteExternalTool';

export const allETLComponentCategories = [
  {
    id: 'source',
    name: 'Sources',
  },
  {
    id: 'llm',
    name: 'LLM and NLP manipulations',
  },
  {
    id: 'biology',
    name: 'Biology-specific transformations or sources',
  },
  {
    id: 'table',
    name: 'Table manipulations',
  },
  {
    id: 'matrix',
    name: 'Matrix manipulations',
  },
  {
    id: 'publications',
    name: 'Publication Calculations',
  },
  {
    id: 'json',
    name: 'JSON parsers',
  },
  {
    id: 'other',
    name: 'Other',
  },
  {
    id: 'dummy',
    name: 'Dummy components for testing',
  },
];

const fixedETLComponentTypeManifests = [
  manifestETLDefineText,
  manifestETLQueryPubMed,
  manifestETLFetchPubMedArticle,
  manifestETLQueryPubTator,
  // manifestETLQueryClinicalTrials,
  manifestETLApplyLLMTable,
  manifestETLApplyLLMText,
  manifestNormalizeGeneNames,
  manifestETLFilterTable,
  manifestETLApplyExpressionTable,
  manifestETLAppendTables,
  manifestETLTableJoin,
  manifestETLTableAggregate,
  manifestETLTablePivot,
  manifestETLImportDataContext,
  manifestETLJsonToTable,
  manifestETLJsonNestedExplode,
  manifestETLTopicClustering,
  manifestETLPublicationCredibility,
  manifestETLSelectDataTable,
  manifestETLTablesToMatrix,
  manifestETLMatrixToTables,
  manifestETLMatrixExtractFeatures,
  manifestETLApplyProjectionUmap,
  manifestETLApplyPcaMatrix,
  manifestETLDgeAnalysis,
  manifestETLApplySummaryStatMatrix,
  manifestETLFilterMatrix,
  manifestETLTableDropColumns,
  manifestQueryTaxonInfo,
];

let manifestLookup: { [id: string]: TpETLComponentTypeManifest } = {};
for (const manifest of fixedETLComponentTypeManifests) manifestLookup[manifest.id] = manifest;

// EXTERNAL COMPONENTS
let etlComponentTypeTemplates: TpETLComponentTypeTemplate[] = [];
let etlExternalComponentTypeManifests: TpETLComponentTypeManifest[] = [];

export function setEtlComponentTypesFromTemplates(templates: TpETLComponentTypeTemplate[]) {
  const manifests = templates.map(getManifestFromTemplate);
  etlComponentTypeTemplates = templates;
  etlExternalComponentTypeManifests = manifests;

  // Update the manifest lookup
  manifestLookup = {};
  for (const manifest of getAllETLComponentTypeManifests()) {
    manifestLookup[manifest.id] = manifest;
  }
}

export function getManifestTemplate(componentType: string): TpETLComponentTypeTemplate {
  const template = etlComponentTypeTemplates.find((template) => template.id === componentType);
  if (!template) {
    throw new Error(`Component type ${componentType} not found`);
  }
  return template;
}

// LOOKUP FUNCTION
export function getAllETLComponentTypeManifests() {
  return [...fixedETLComponentTypeManifests, ...etlExternalComponentTypeManifests];
}

export function getETLComponentType(compType: string): TpETLComponentTypeManifest {
  if (!manifestLookup[compType]) throw getUserError(`Invalid component type: ${compType}`);
  return manifestLookup[compType];
}
